'use client';

import { useLocale, useTranslations } from 'next-intl';
import { Link } from '@/src/i18n/navigation';
import { BasicProductDataResponse, DiscountTypeEnums } from '@/src/types/catalog';
import Image from '@/src/components/Image';
import { cn, PRODUCT_IMAGE_SIZE } from '@/src/lib/utils/command';
import { useParams } from 'next/navigation';
import { Routes } from '@/src/routes/routes';
import IsFavorite from '../../../../components/IsFavorite';
import SaudiRealls, { TCountryId, TCurrency } from '@/src/components/sarComponent';
import { trackEvent } from '@/src/lib/amplitude';
import { AmplitudeEvents } from '@/src/lib/amplitude/events';
import Modal from './Modal';
import Typography from '@/src/components/ui/typography';
import { useCountryStore } from '@/src/lib/state-stores/currencyStore';
import { Badge } from '@/src/components/ui/Badge';
import ExpressIcon from '@/src/components/icons/ExpressIcon';
// import { extractIdFromSlug, extractNameFromSlug } from '@/lib/utils/slugify';

interface ProductCardHandlers {
  scroll?: boolean;
  isFavorite?: boolean;
  isSearchPage?: boolean;
  branchId?: string | number;
  branchCurrency?: TCurrency;
  branchCountryId?: TCountryId;
  storeTime?: string;
}

export function ProductCard({
  productDetails,
  scroll = false,
  isSearchPage = false,
  branchId,
  branchCountryId,
  branchCurrency,
  storeTime,
}: {
  productDetails: BasicProductDataResponse;
} & ProductCardHandlers) {
  const {
    defaultImage,
    isHaveValidDiscount,
    discount,
    price,
    englishName,
    arabicName,
    originalPrice,
    isSoldOut,
    storeName,
    currencyCode,
    storeEnglishName,
    storeArabicName,
    branchName,
    countryId,
  } = productDetails as BasicProductDataResponse;

  const { storeId, store } = useParams();

  const decodedStore = decodeURIComponent(store as string);
  const { countryId: storedCountryId } = useCountryStore();

  const currentLang = useLocale() as 'ar' | 'en';
  const t = useTranslations('product');

  const discountPercentage =
    discount?.discountType === DiscountTypeEnums.Percentage && discount?.discountValue
      ? Math.round(discount.discountValue)
      : originalPrice && price && originalPrice > price
        ? Math.round(((originalPrice - price) / originalPrice) * 100)
        : 0;

  return (
    <figure
      className={cn(
        'relative flex h-full w-full flex-col justify-between overflow-hidden rounded-t-xl bg-white',
      )}
    >
      <Link
        href={Routes.productDetails(
          String(branchId || productDetails.branchId),
          String(storeEnglishName || decodedStore),
          productDetails.integrationId,
          productDetails.englishName || '',
        )}
        onClick={() => {
          trackEvent(AmplitudeEvents.product_clicked, {
            shop_id: Number(storeId),
            shop_name: productDetails.storeName,
            branch_id: Number(branchId || productDetails.branchId),
            branch_name: productDetails.branchName,
            product_id: productDetails.integrationId,
            product_name: currentLang === 'en' ? englishName : arabicName,
            has_discount: productDetails?.isHaveValidDiscount || false,
          });
        }}
        className="h-full"
      >
        <div className={`relative aspect-square w-full overflow-hidden rounded-xl`}>
          <Image
            size={PRODUCT_IMAGE_SIZE.MEDIUM}
            alt={`${productDetails.englishName}`}
            src={defaultImage || ''}
            key={defaultImage || ''}
            fill
            className="object-cover"
            sizes="(min-width: 1420px) 452px, (min-width: 1040px) calc(29.72vw + 36px), (min-width: 780px) calc(41.67vw - 20px), (min-width: 640px) 52.5vw, 78.75vw"
          />

          {/* handle the descound and the fav  */}

          <div className="absolute top-0 left-0 z-10 flex w-full items-center justify-between px-2 pt-2 flex-row-reverse">
            {/* is fav  */}
            <div
              className="grid h-8 w-8 place-items-center rounded-full bg-white/80 shadow-md backdrop-blur-md"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <IsFavorite
                branch_id={
                  productDetails?.branchId ?? storeId?.toString() ?? branchId?.toString() ?? ''
                }
                item_id={productDetails.integrationId}
                size={18}
                type={1}
              />
            </div>

            <div className="flex flex-col gap-2">
              {/* Start: Discount status */}
              {isHaveValidDiscount ? (
                <Badge variant="red" className="font-normal">
                  {discountPercentage > 0 ? `${t('sale')} ${discountPercentage}%` : t('sale')}
                </Badge>
              ) : null}

              {/* if hase a store time  */}

              {storeTime ? (
                <Badge variant="time">
                  <ExpressIcon /> {storeTime}
                </Badge>
              ) : null}
            </div>
          </div>

          {/* start slod out section  */}
          {isSoldOut && (
            <div className="bg-primary/50 absolute flex h-full w-full items-center">
              <span className="z-30 flex w-full justify-center bg-[#A6A6A6]! py-1 text-white">
                {t('SoldOut')}
              </span>
            </div>
          )}
          {/* End: Over the Image content */}
        </div>

        <figcaption className="flex flex-col justify-between p-2 lg:p-3">
          {/* content */}
          <div className="flex flex-col items-start gap-1">
            {/* store name */}
            <Typography variant={'body2'} color={'gray'} component={'h3'} align={'start'}>
              {currentLang === 'ar' ? storeArabicName : storeEnglishName}
            </Typography>

            {/* product name */}
            <Typography
              color={'black'}
              component={'h2'}
              lineClamp={2}
              align={'start'}
              variant={'subtitle2'}
            >
              {currentLang === 'en' ? englishName : arabicName}
            </Typography>
          </div>
        </figcaption>

        {/* End: Price  */}
      </Link>
      {/* Add to card  */}
      {/* price  */}
      <div className="flex flex-col px-2 pb-2 font-semibold">
        {/* discount  */}
        <div className="text-gray">
          {isHaveValidDiscount && (
            <SaudiRealls
              price={Number(originalPrice?.toFixed(2))}
              size="extraSmall"
              isHaveDiscount={true}
              currency={currencyCode || (branchCurrency as TCurrency)}
              countryId={countryId || (branchCountryId as TCountryId) || storedCountryId}
            />
          )}
        </div>

        {/* price after descount  */}
        <div className="flex items-center">
          <span className={cn(isHaveValidDiscount && 'text-[#DE1135]')}>
            <SaudiRealls
              price={Number(price?.toFixed(2))}
              size="small"
              currency={currencyCode || (branchCurrency as TCurrency)}
              countryId={countryId || (branchCountryId as TCountryId) || storedCountryId}
            />
          </span>
          {isHaveValidDiscount && (
            <div className="flex items-center gap-1 px-1 py-1 text-xs text-[#DE1135]">
              (
              {discount?.discountType === DiscountTypeEnums.Percentage ? (
                <>{discount?.discountValue}% </>
              ) : (
                <SaudiRealls
                  price={Number(discount?.discountValue)}
                  size="extraSmall"
                  currency={currencyCode || branchCurrency}
                  countryId={countryId || (branchCountryId as TCountryId) || storedCountryId}
                />
              )}
              {t('Off')} )
            </div>
          )}{' '}
        </div>
      </div>
      {isSearchPage && (
        <>
          {isSoldOut ? (
            <button className="text-gray w-full border-t py-2 text-xs md:text-sm" disabled>
              {t('SoldOut')}
            </button>
          ) : (
            <Modal
              productId={productDetails.integrationId}
              storeId={productDetails.branchId as string}
            />
          )}
        </>
      )}{' '}
    </figure>
  );
}
